import React, { useEffect } from 'react';
import moment from 'moment';
import { titleCase, formatValue } from '../common/utils';

import { api } from '../../../services/api';
import { session } from '../../../services/session';
import { dateService } from '../../../services/date';

export class PaymentsService {
  constructor() {
    this.Api = api;
    this.Session = session;

    this.earningsColumns = [
      {
        dataKey: 'merchant_name',
        label: 'Merchant',
        width: 3,
        sortable: true,
      },
      {
        dataKey: 'earnings_month',
        label: 'Earnings Month',
        width: 2,
        justifyContent: 'right',
        sortable: true,
      },
      {
        dataKey: 'payment_status',
        label: 'Status',
        width: 2,
        fontColor: cellData =>
          cellData.toLowerCase().includes('overdue')
            ? 'red'
            : cellData.toLowerCase().includes('payment') || cellData.toLowerCase().includes('paid')
            ? 'green'
            : '',
        justifyContent: 'right',
        sortable: false,
        cellRender: ({ cellData }) => (
          <div style={{ whiteSpace: 'normal', textAlign: 'right' }}>{cellData}</div>
        ),
      },
      {
        dataKey: 'publisher_earnings',
        label: 'Amount',
        width: 1,
        justifyContent: 'right',
        sortable: true,
      },
    ];

    this.payoutsColumns = [
      {
        dataKey: 'payout_date',
        label: 'Payout Date',
        width: 4,
        tooltip: () => ({
          text: 'View breakdown by Merchant',
          size: 200,
          fixedSize: true,
          noTail: true,
        }),
        cellRender: ({ cellData }) => dateService.formatDisplayDate(cellData),
        sortable: true,
      },
      {
        dataKey: 'transfer_status',
        label: 'Status',
        width: 2,
        fontColor: cellData => (cellData.toLowerCase() === 'paid' ? 'green' : ''),
        justifyContent: 'right',
        sortable: true,
      },
      {
        dataKey: 'payout_amount',
        label: 'Amount',
        width: 1,
        justifyContent: 'right',
        sortable: true,
      },
    ];

    this.payoutsBreakdownColumns = [
      {
        dataKey: 'transfer_date',
        label: 'Transaction Date',
        width: 4,
        sortable: true,
      },
      {
        dataKey: 'merchant_name',
        label: 'Merchant',
        width: 2,
        justifyContent: 'left',
        sortable: true,
      },
      {
        dataKey: 'earnings_month',
        label: 'Earnings Month',
        width: 1,
        justifyContent: 'right',
        sortable: true,
      },
      {
        dataKey: 'amount_usd',
        label: 'Amount',
        width: 1.8,
        justifyContent: 'right',
        sortable: true,
      },
    ];
  }

  checkIfBillingUser() {
    if (this.Session && this.Session.accessList) {
      const orgPerms = this.Session.accessList.find(org => org.orgId === this.Session.orgId);
      return orgPerms && orgPerms.isBillingUser;
    }
    return false;
  }

  getAccountLink(publisherId) {
    return api.get(`/api/v0/billing/account_link/${publisherId}/`);
  }

  showAlertIcon(accountLink) {
    return ['unconnected', 'disabled', 'descriptive-error'].includes(this.getStatus(accountLink));
  }

  getStatus(link) {
    if (!Object.keys(link).length) return null;

    return !link.payouts_enabled &&
      !link.currently_due &&
      (link.disabled_reason === 'requirements.pending_verification' || link.pending_verification)
      ? 'pending'
      : link.account_setup_complete && !link.payouts_enabled
      ? 'disabled'
      : link.currently_due || link.errors
      ? 'descriptive-error'
      : !link.account_setup_complete && !link.payouts_enabled
      ? 'unconnected'
      : 'success';
  }

  getEarnings(publisherId, params) {
    const url = `/api/v0/billing/earnings/${publisherId}/`;
    return api.get(url, params);
  }

  getPayouts(publisherId, params) {
    const url = `/api/v0/billing/payouts/${publisherId}/`;
    return api.get(url, params);
  }

  getPayoutsBreakdown(publisherId, params) {
    const url = `/api/v0/billing/payouts/transfers/${publisherId}/`;
    return api.get(url, params);
  }

  populateDateSelector() {
    const monthDropdownOptions = [{ label: 'Lifetime', params: {} }];

    let monthDropdownItems = 6;
    while (monthDropdownItems >= 0) {
      const dateObj = moment().subtract(monthDropdownItems, 'months');
      monthDropdownItems--;

      const monthOption = {
        label: dateObj.format('MMMM YYYY'),
        params: {
          month: parseInt(dateObj.format('M')),
          year: parseInt(dateObj.format('YYYY')),
        },
      };
      monthDropdownOptions.push(monthOption);
    }
    return monthDropdownOptions.reverse();
  }

  formatEarningsData(data) {
    return data.data.map((payment, index) => ({
      key: index,
      ...payment,
      earnings_month: dateService.formatMonthYear(payment.earnings_month),
      publisher_earnings: formatValue(payment.publisher_earnings, 'currency'),
      merchant_name: titleCase(payment.merchant_name),
    }));
  }

  formatPayoutsData(data) {
    const payoutStatusMap = { '1': 'Scheduled', '3': 'Paid' };

    const payouts = data.payout_sums.map((data, index) => {
      // TODO examine formatDisplayDate warning in console
      return {
        key: index,
        payout_amount: formatValue(data.payout_amount, 'currency'),
        transfer_status: payoutStatusMap[data.transfer_status],
        payout_date: data.payout_date,
      };
    });

    const { month, year } = data.date;
    const dateLabel = month && year ? ` (${dateService.formatMonthYear(`${year}-${month}`)})` : '';

    const payoutTotalCard = {
      title: `Total Amount Paid${dateLabel}`,
      value: formatValue(data.totals.total_payout_sum, 'currency'),
      label: 'Paid',
    };

    const expectedPayoutCard = {
      title: 'Next Expected Payout',
      value: formatValue(data.totals.total_scheduled_sum, 'currency'),
      label: 'Scheduled',
    };

    return {
      payouts,
      cardData: [payoutTotalCard, expectedPayoutCard],
    };
  }

  formatPayoutsBreakdownData(data) {
    return data.map((payout, index) => ({
      ...payout,
      key: index,
      transfer_date: dateService.formatDisplayDate(payout.transfer_date),
      earnings_month: dateService.formatMonthYear(payout.earnings_month),
      amount_usd: formatValue(payout.amount_usd, 'currency'),
      merchant_name: titleCase(payout.merchant_name),
    }));
  }

  formatEarningsCardData(data) {
    const { date } = data;
    const dateLabel = date
      ? date.invoice_year && date.invoice_month
        ? `In ${dateService.formatMonthYear(`${date.invoice_year}-${date.invoice_month}`)}`
        : 'In Lifetime'
      : ' ';

    const cardLabels = [
      { title: 'Total Amount Earned', label: dateLabel, value: data.totals.total_earnings },
      { title: 'Total Amount Paid', label: 'Paid', value: data.totals.success_amount },
      { title: 'Outstanding Payments', label: 'Not Due Yet', value: data.totals.not_due_amount },
      { title: 'Outstanding Payments', label: 'Overdue', value: data.totals.outstanding_amount },
    ];

    //	any metric with the value of 0 will not be displayed in a card
    return cardLabels.reduce(
      (acc, curr) =>
        parseFloat(curr.value)
          ? [...acc, { ...curr, value: formatValue(curr.value, 'currency') }]
          : acc,
      [],
    );
  }

  getMockData() {
    return {
      earningsRes: {
        pub_id: 1000,
        date: {
          invoice_month: 5,
          invoice_year: 2020,
        },
        totals: {
          // Totals power the header cards on the earnings tab
          total_earnings: '40.00',
          outstanding_amount: '40.00',
          success_amount: '40.00',
          not_due_amount: '40.00',
        },
        data: [
          {
            merchant_name: 'merch1',
            payment_status: 'Payment Initiated on August 2020',
            publisher_earnings: '40.00',
          },
          {
            merchant_name: 'merch2',
            payment_status: 'Overdue on August 2020',
            publisher_earnings: '400.00',
          },
        ],
      },
      payoutsRes: {
        pub_id: 1000,
        date: {
          month: 12,
          year: 2020,
        },
        totals: {
          total_transfer_sum: '345.00',
          total_scheduled_sum: '200.00',
          total_payout_sum: '145.00',
        },
        payout_sums: [
          {
            payout_date: '2020-12-01',
            transfer_status: 1,
            payout_amount: '75.00',
          },
          {
            payout_date: '2020-12-31',
            transfer_status: 1,
            payout_amount: '30.00',
          },
          {
            payout_date: '2020-12-31',
            transfer_status: 3,
            payout_amount: '40.00',
          },
          {
            payout_date: '2021-01-31',
            transfer_status: 3,
            payout_amount: '240.00',
          },
        ],
        page_info: {
          total_items: 4,
        },
      },
      payoutsBreakdownRes: {
        pub_id: 1000,
        date: '2020-12-31',
        total: '70.00',
        payments: [
          {
            transfer_id: 'tran_4000',
            transfer_date: '2021-06-23',
            merchant_name: 'merch1',
            earnings_month: '2020-10-01',
            amount_usd: '40.00',
          },
          {
            transfer_id: 'tran_5000',
            transfer_date: '2021-06-23',
            merchant_name: 'merch1',
            earnings_month: '2020-10-01',
            amount_usd: '30.00',
          },
        ],
      },
    };
  }
}

export const paymentsService = new PaymentsService();
